import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Container,
  Text,
  VStack,
  SimpleGrid,
  Icon,
  Avatar,
  HStack,
} from "@chakra-ui/react";
import {
  FaQuoteLeft,
  FaStar,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import CountUp from "../UX/CountUp";
import female from "../../assets/female.jpg";
import male from "../../assets/male.jpg";

const testimonials = [
  {
    id: 1,
    name: "Den Kyle I. Dantes",
    role: "CEO & General Manager, DKD Marketing",
    content:
      "Transformed our outdated website into a modern masterpiece. Delivered a seamless user experience and beautiful design.",
    rating: 5,
    avatar: male,
  },
  {
    id: 2,
    name: "Samantha Manubag",
    role: "Client",
    content:
      "Can develop a website that lives up to the expectations of the client. He can work fast and was able to give the clients the input on time",
    rating: 5,
    avatar: female,
  },
  // {
  //   id: 3,
  //   name: "Sarah Chen",
  //   role: "Founder, DesignHub",
  //   content:
  //     "A true professional who understands both design and development. Delivered a beautiful, functional product on time.",
  //   rating: 5,
  //   avatar: "https://i.pravatar.cc/100?img=5",
  // },
  // {
  //   id: 4,
  //   name: "Emily Watson",
  //   role: "Marketing Director, GrowthGen",
  //   content:
  //     "Outstanding work! The website exceeded our expectations. The attention to detail and creative solutions made our project a huge success.",
  //   rating: 5,
  //   avatar: "https://i.pravatar.cc/100?img=9",
  // },
];

const TestimonialCard = ({ testimonial, direction }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const animationDelay = 0.5 * 0.5;

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    card.style.setProperty("--mouse-x", `${x}px`);
    card.style.setProperty("--mouse-y", `${y}px`);
    setMousePos({ x, y });
  };

  return (
    <motion.div
      key={testimonial.id}
      initial={{
        opacity: 0,
        y: direction === "up" ? 80 : -80,
        scale: 0.92,
        rotateX: direction === "up" ? -20 : 20,
        filter: "blur(4px)",
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        rotateX: 0,
        filter: "blur(0px)",
      }}
      exit={{
        opacity: 0,
        y: direction === "up" ? -80 : 80,
        scale: 0.92,
        rotateX: direction === "up" ? 20 : -20,
        filter: "blur(4px)",
      }}
      transition={{ duration: 0.7, delay: animationDelay, ease: "easeInOut" }}
      style={{
        width: "100%",
      }}
      className="card bg-grey-rgba rounded-lg border-2 border-[#ffffff1a] relative w-full py-14 px-8"
      bg="rgba(255, 255, 255, 0.05)"
      borderRadius="lg"
      padding="4em 2em"
      border="2px"
      borderColor="#ffffff1a"
      position="relative"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        bg={`radial-gradient(400px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.02), transparent 40%)`}
        zIndex="1"
        borderRadius="inherit"
        pointerEvents="none"
        transition="opacity 500ms"
        opacity={isHovered ? 1 : 0}
      />
      <Box
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        bg={`radial-gradient(200px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.09), transparent 40%)`}
        zIndex="1"
        borderRadius="inherit"
        pointerEvents="none"
        transition="opacity 500ms"
        opacity={isHovered ? 1 : 0}
      />
      <Box
        position="absolute"
        top="-50%"
        left="-50%"
        width="200%"
        height="200%"
        bg="radial-gradient(circle at center, rgba(103, 120, 203, 0.05), transparent 50%)"
        pointerEvents="none"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.5, rotate: -30 }}
        animate={{ opacity: 0.1, scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        style={{ position: "absolute", top: 16, right: 16 }}
      >
        <Icon as={FaQuoteLeft} w={16} h={16} color="#6778cb" />
      </motion.div>

      <VStack spacing={6} position="relative" zIndex={1}>
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <HStack spacing={4}>
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Box
                borderRadius="full"
                p="2px"
                bg="linear-gradient(135deg, #6778cb, #a855f7)"
                as={motion.div}
                animate={{
                  background: [
                    "linear-gradient(135deg, #6778cb, #a855f7)",
                    "linear-gradient(135deg, #a855f7, #6778cb)",
                    "linear-gradient(135deg, #6778cb, #a855f7)",
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <Avatar
                  src={testimonial.avatar}
                  name={testimonial.name}
                  size={{ base: "md", md: "lg" }}
                  border="3px solid #0a0a0f"
                />
              </Box>
            </motion.div>
            <VStack align="start" spacing={0}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              >
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{ base: "md", md: "lg" }}
                >
                  {testimonial.name}
                </Text>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                  {testimonial.role}
                </Text>
              </motion.div>
            </VStack>
          </HStack>
        </motion.div>

        <HStack spacing={1}>
          {[...Array(testimonial.rating)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.5 + i * 0.1,
                type: "spring",
                stiffness: 200,
              }}
              whileHover={{
                scale: 1.3,
                rotate: 15,
                transition: { type: "spring", stiffness: 300 },
              }}
            >
              <Icon
                as={FaStar}
                w={{ base: 4, md: 5 }}
                h={{ base: 4, md: 5 }}
                color="#6778cb"
              />
            </motion.div>
          ))}
        </HStack>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Text
            color="gray.300"
            fontSize={{ base: "sm", md: "lg" }}
            textAlign="center"
            lineHeight="1.8"
            fontStyle="italic"
          >
            "{testimonial.content}"
          </Text>
        </motion.div>
      </VStack>
    </motion.div>
  );
};

const About = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [direction, setDirection] = useState("up");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const animationDelay = 0.5 * 0.5;

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePos({ x, y });
  };

  const nextTestimonial = () => {
    setDirection("up");
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setDirection("down");
    setCurrentTestimonial(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setDirection("up");
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovered]);

  const stats = [
    { number: 20, suffix: "+", label: "Projects" },
    { number: 6, suffix: "+", label: "Satisfied Clients" },
    { number: 4, suffix: "+", label: "Years Exp" },
    { number: 100, suffix: "%", label: "Passion" },
  ];

  return (
    <Box id="about" className="about-section relative z-10" py={24}>
      <Container sx={{ paddingX: "0 !important" }} maxW="100%">
        <VStack spacing={12}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative w-full"
          >
            <h2 className="text-txt text-xl sm:text-3xl lg:text-5xl font-normal text-center">
              Why Choose Me
            </h2>
          </motion.div>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} width="100%">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <VStack spacing={2}>
                  <Text
                    fontSize={{ sm: "lg", md: "2xl", lg: "5xl" }}
                    fontWeight="bold"
                    color="#fff"
                    display="flex"
                    alignItems="baseline"
                    justifyContent="center"
                  >
                    <CountUp
                      to={stat.number}
                      from={0}
                      duration={2}
                      delay={index * 0.2}
                      className="count-up-number"
                    />
                    <Text
                      as="span"
                      fontSize={{ sm: "lg", md: "2xl", lg: "5xl" }}
                      fontWeight="bold"
                      color="#fff"
                    >
                      {stat.suffix}
                    </Text>
                  </Text>
                  <Text color="gray.400" fontSize="md" textAlign="center">
                    {stat.label}
                  </Text>
                </VStack>
              </motion.div>
            ))}
          </SimpleGrid>

          <motion.p
            initial={{ opacity: 0 }}
            viewport={{ once: true }}
            whileInView={{
              opacity: 1,
            }}
            transition={{ duration: 1, delay: 0.4, ease: "easeInOut" }}
            className="text-zinc-500 text-[1rem] sm:text-2xl lg:text-4xl 2xl:px-48 font-light mb-8 md:mb-12 2xl:text-center"
          >
            As a full-stack developer, UI/UX skilled, and graphic designer, I
            blend my technical expertise with a strong artistic vision to
            develop seamless, engaging, and visually appealing digital
            experiences.
          </motion.p>

          <Box
            width="100%"
            maxW="900px"
            marginTop="4em"
            position="relative"
            overflow="hidden"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <VStack spacing={2} mb={6}>
                <h2 className="text-txt text-xl sm:text-3xl lg:text-5xl font-normal 2xl:text-center">
                  What Clients Say
                </h2>
              </VStack>
            </motion.div>

            <HStack spacing={6} align="stretch" width="100%">
              <Box
                flex={1}
                position="relative"
                minHeight={{ base: "300px", md: "350px" }}
              >
                <AnimatePresence mode="wait">
                  <TestimonialCard
                    key={testimonials[currentTestimonial].id}
                    testimonial={testimonials[currentTestimonial]}
                    direction={direction}
                  />
                </AnimatePresence>
              </Box>

              <VStack
                spacing={0}
                justify="center"
                align="center"
                display={{ base: "none", md: "flex" }}
                width="60px"
                py={4}
              >
                <motion.button
                  onClick={prevTestimonial}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "12px",
                    width: "45px",
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#6778cb",
                    marginBottom: "12px",
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Icon as={FaChevronUp} w={5} h={5} />
                  </motion.div>
                </motion.button>

                <VStack spacing={2} my={2}>
                  {testimonials.map((_, index) => (
                    <motion.button
                      key={index}
                      onClick={() => {
                        setDirection(
                          index > currentTestimonial ? "up" : "down",
                        );
                        setCurrentTestimonial(index);
                      }}
                      animate={{
                        width: index === currentTestimonial ? 8 : 6,
                        height: index === currentTestimonial ? 24 : 6,
                        background:
                          index === currentTestimonial
                            ? "#6778cb"
                            : "rgba(255, 255, 255, 0.2)",
                      }}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        borderRadius: "3px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </VStack>

                <motion.button
                  onClick={nextTestimonial}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "12px",
                    width: "45px",
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#6778cb",
                    marginTop: "12px",
                  }}
                >
                  <motion.div
                    animate={{ y: [0, 3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Icon as={FaChevronDown} w={5} h={5} />
                  </motion.div>
                </motion.button>
              </VStack>
            </HStack>

            <HStack
              justify="center"
              spacing={2}
              mt={6}
              display={{ base: "flex", md: "none" }}
            >
              <motion.button
                onClick={prevTestimonial}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#6778cb",
                }}
              >
                <Icon as={FaChevronUp} w={3} h={3} />
              </motion.button>

              <HStack spacing={1.5} mx={1}>
                {testimonials.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => {
                      setDirection(index > currentTestimonial ? "up" : "down");
                      setCurrentTestimonial(index);
                    }}
                    animate={{
                      width: index === currentTestimonial ? 16 : 5,
                      height: 5,
                      background:
                        index === currentTestimonial
                          ? "#6778cb"
                          : "rgba(255, 255, 255, 0.2)",
                    }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      borderRadius: "2.5px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </HStack>

              <motion.button
                onClick={nextTestimonial}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "8px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#6778cb",
                }}
              >
                <Icon as={FaChevronDown} w={3} h={3} />
              </motion.button>
            </HStack>
          </Box>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 1,
              delay: animationDelay,
              ease: "easeInOut",
            }}
            position="relative"
            p={{ base: 6, sm: 10 }}
            bg="rgba(255, 255, 255, 0.05)"
            borderRadius="lg"
            border="2px"
            borderColor="#ffffff1a"
            textAlign="center"
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="w-full"
          >
            <motion.div
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: `radial-gradient(400px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.02), transparent 40%)`,
                zIndex: 1,
                borderRadius: "inherit",
                pointerEvents: "none",
              }}
            />
            <motion.div
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: `radial-gradient(200px circle at ${mousePos.x}px ${mousePos.y}px, rgba(255, 255, 255, 0.09), transparent 40%)`,
                zIndex: 1,
                borderRadius: "inherit",
                pointerEvents: "none",
              }}
            />
          </motion.div>
        </VStack>
      </Container>
    </Box>
  );
};

export default About;
